import type { OwnerTask, OwnerTaskPlace } from "@szyos/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useId, useState, type FormEvent } from "react";
import { api, errorMessage } from "../api";
import { Icon } from "../icons";
import { ErrorNote, useToast } from "./feedback";

const PLACE_LABEL: Record<OwnerTaskPlace, string> = {
  vps: "VPS per PuTTY",
  plesk: "Plesk",
  pc: "Dein PC",
  claude_app: "Claude-App",
  szyos: "szyOS",
  other: "Sonstiges",
};

/** Befehlszeile mit Knopf zum Kopieren; `$ ` am Anfang markiert einen Befehl. */
function Command({ text }: { text: string }) {
  const toast = useToast();
  const copy = () => {
    navigator.clipboard
      .writeText(text)
      .then(() => toast("Befehl kopiert."))
      .catch(() => toast("Kopieren ging nicht. Bitte den Befehl markieren und kopieren.", "error"));
  };
  return (
    <div className="todo-cmd">
      <code>{text}</code>
      <button type="button" className="chip-btn" onClick={copy} aria-label={`Befehl kopieren: ${text}`}>
        <Icon name="file" /> Kopieren
      </button>
    </div>
  );
}

function TaskItem({ task, projectTitle }: { task: OwnerTask; projectTitle: string | null }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const done = Boolean(task.doneAt);
  const toggle = useMutation({
    mutationFn: () => api.setOwnerTaskDone(task.id, !done),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["owner-tasks"] });
      toast(done ? "Wieder offen." : "Erledigt. Danke!");
    },
    onError: (err) => toast(errorMessage(err), "error"),
  });
  return (
    <li className={`todo${done ? " is-done" : ""}${task.urgent && !done ? " is-urgent" : ""}`}>
      <label className="todo-head">
        <input type="checkbox" checked={done} disabled={toggle.isPending} onChange={() => toggle.mutate()} />
        <span>
          <strong>{task.title}</strong>
          <span className="todo-meta">
            {task.urgent && !done && <span className="chip chip--alert">Dringend</span>}
            <span className="chip chip--ghost">{PLACE_LABEL[task.place]}</span>
            {projectTitle && task.projectId && (
              <Link to="/projects/$projectId" params={{ projectId: task.projectId }} className="link">
                {projectTitle}
              </Link>
            )}
            {task.workOrderId && (
              <Link to="/work-orders/$orderId" params={{ orderId: task.workOrderId }} className="link">
                Zum Auftrag
              </Link>
            )}
          </span>
        </span>
      </label>
      {!done && (task.note || task.steps.length > 0 || task.autoCheckLabel) && (
        <div className="todo-body">
          {task.note && <p className="muted">{task.note}</p>}
          {task.autoCheckLabel && (
            <p className="muted">
              <Icon name="check" /> {task.autoCheckLabel}
            </p>
          )}
          {task.steps.length > 0 && (
            <ol>
              {task.steps.map((step, i) => (
                <li key={`${i}-${step}`}>
                  {step.startsWith("$ ") ? <Command text={step.slice(2)} /> : step}
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </li>
  );
}

function AddTask({ projects }: { projects: { id: string; title: string }[] }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const ids = { title: useId(), place: useId(), project: useId(), steps: useId() };
  const [title, setTitle] = useState("");
  const [place, setPlace] = useState<OwnerTaskPlace>("vps");
  const [projectId, setProjectId] = useState("");
  const [steps, setSteps] = useState("");
  const [urgent, setUrgent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () =>
      api.createOwnerTask({
        title: title.trim(),
        place,
        urgent,
        projectId: projectId || null,
        steps: steps
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean),
      }),
    onSuccess: async () => {
      setTitle("");
      setSteps("");
      setUrgent(false);
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ["owner-tasks"] });
      toast("Eintrag angelegt.");
    },
    onError: (err) => setError(errorMessage(err)),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return setError("Titel angeben.");
    create.mutate();
  };
  return (
    <details className="todo-group todo-add">
      <summary>Eintrag hinzufügen</summary>
      <form className="form" onSubmit={submit} noValidate>
        <label htmlFor={ids.title}>Was ist zu tun?</label>
        <input id={ids.title} value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
        <label htmlFor={ids.place}>Wo?</label>
        <select id={ids.place} value={place} onChange={(e) => setPlace(e.target.value as OwnerTaskPlace)}>
          {(Object.keys(PLACE_LABEL) as OwnerTaskPlace[]).map((p) => (
            <option key={p} value={p}>
              {PLACE_LABEL[p]}
            </option>
          ))}
        </select>
        <label htmlFor={ids.project}>Projekt (optional)</label>
        <select id={ids.project} value={projectId} onChange={(e) => setProjectId(e.target.value)}>
          <option value="">kein Projekt</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.title}
            </option>
          ))}
        </select>
        <label htmlFor={ids.steps}>Schritte, einer je Zeile; Befehle mit „$ “ beginnen</label>
        <textarea id={ids.steps} rows={4} value={steps} onChange={(e) => setSteps(e.target.value)} />
        <label className="todo-urgent">
          <input type="checkbox" checked={urgent} onChange={(e) => setUrgent(e.target.checked)} /> Dringend
        </label>
        <ErrorNote error={error} />
        <button type="submit" className="btn btn--primary" disabled={create.isPending}>
          <span className="btn-label">
            <Icon name="plus" /> Hinzufügen
          </span>
        </button>
      </form>
    </details>
  );
}

/**
 * „Für dich zu tun“ im Cockpit (UX-12): was nur du außerhalb von szyOS erledigen kannst, mit Befehlen zum
 * Kopieren; dazu wartende Auslieferungen von Hand aus den Entscheidungen.
 */
export function OwnerTasksPanel() {
  const tasks = useQuery({ queryKey: ["owner-tasks"], queryFn: api.ownerTasks });
  const projects = useQuery({ queryKey: ["projects"], queryFn: api.projects });
  const decisions = useQuery({ queryKey: ["decisions"], queryFn: () => api.decisions("PENDING") });
  const titles = new Map((projects.data ?? []).map((p) => [p.id, p.title]));
  const filterId = useId();
  const [projectFilter, setProjectFilter] = useState("");
  const all = tasks.data ?? [];
  const taskProjects = [...new Set(all.map((t) => t.projectId ?? ""))]
    .map((key) => [key, key ? (titles.get(key) ?? "Projekt") : "Ohne Projekt"] as const)
    .sort((a, b) => Number(a[0] === "") - Number(b[0] === "") || a[1].localeCompare(b[1], "de"));
  const list = projectFilter ? all.filter((t) => (t.projectId ?? "") === projectFilter) : all;
  const open = list.filter((t) => !t.doneAt);
  // Erledigtes der letzten sieben Tage bleibt sichtbar, Älteres wandert ins Archiv (Wunsch des Owners 24.10.).
  const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const done = list.filter((t) => t.doneAt && Date.parse(t.doneAt) >= weekAgo);
  const archived = list.filter((t) => t.doneAt && Date.parse(t.doneAt) < weekAgo);
  const manual = (decisions.data ?? []).filter((d) => d.state === "PENDING" && d.checklist.length > 0);
  // Je Projekt eine Gruppe, dringende Gruppen zuerst, dann nach Projektname; Einträge ohne Projekt zuletzt.
  const byProject = new Map<string, OwnerTask[]>();
  for (const t of open) byProject.set(t.projectId ?? "", [...(byProject.get(t.projectId ?? "") ?? []), t]);
  const groups = [...byProject.entries()]
    .map(([key, items]) => ({
      key,
      title: key ? (titles.get(key) ?? "Projekt") : "Ohne Projekt",
      tasks: items,
    }))
    .sort(
      (a, b) =>
        Number(b.tasks.some((t) => t.urgent)) - Number(a.tasks.some((t) => t.urgent)) ||
        Number(a.key === "") - Number(b.key === "") ||
        a.title.localeCompare(b.title, "de"),
    );

  return (
    <section className="panel panel--section todo-panel" id="fuer-dich" aria-labelledby="todoHead">
      <div className="panel-head">
        <h2 id="todoHead">Für dich zu tun</h2>
        <span className="muted" role="status">
          {open.length + manual.length === 0 ? "Nichts offen." : `${open.length + manual.length} offen`}
        </span>
      </div>
      {taskProjects.length > 1 && (
        <div className="list-filter">
          <label htmlFor={filterId}>Projekt</label>
          <select id={filterId} value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}>
            <option value="">Alle Projekte</option>
            {taskProjects.map(([key, title]) => (
              <option key={key || "none"} value={key}>
                {title}
              </option>
            ))}
          </select>
        </div>
      )}
      {manual.length > 0 && (
        <details className="todo-group" open>
          <summary>
            <strong>Auslieferungen von Hand</strong> <span className="muted">({manual.length} offen)</span>
          </summary>
          <ul className="todo-list">
            {manual.map((d) => (
              <li key={d.id} className="todo">
                <span className="todo-head">
                  <Icon name="rocket" />
                  <span>
                    <strong>{d.question}</strong>
                    <span className="todo-meta">
                      <span className="chip chip--ghost">Checkliste, {d.checklist.length} Schritte</span>
                      <Link to="/decisions" className="link">
                        Zur Entscheidung
                      </Link>
                    </span>
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
      {groups.map((g) => (
        // Zugeklappt, damit die Liste kurz bleibt (Wunsch des Owners 24.10.); Dringendes steht in der Zusammenfassung.
        <details key={g.key} className="todo-group">
          <summary>
            <strong>{g.title}</strong>{" "}
            <span className="muted">
              ({g.tasks.length} offen{g.tasks.some((t) => t.urgent) ? ", dringend" : ""})
            </span>
          </summary>
          <ul className="todo-list">
            {g.tasks.map((t) => (
              <TaskItem key={t.id} task={t} projectTitle={null} />
            ))}
          </ul>
        </details>
      ))}
      {done.length > 0 && (
        <details className="todo-group todo-done">
          <summary>Zuletzt erledigt ({done.length})</summary>
          <ul className="todo-list">
            {done.map((t) => (
              <TaskItem
                key={t.id}
                task={t}
                projectTitle={t.projectId ? (titles.get(t.projectId) ?? null) : null}
              />
            ))}
          </ul>
        </details>
      )}
      {archived.length > 0 && (
        <details className="todo-group todo-done">
          <summary>
            Archiv ({archived.length}) <span className="muted">älter als eine Woche</span>
          </summary>
          <ul className="todo-list">
            {archived.map((t) => (
              <TaskItem
                key={t.id}
                task={t}
                projectTitle={t.projectId ? (titles.get(t.projectId) ?? null) : null}
              />
            ))}
          </ul>
        </details>
      )}
      <AddTask projects={(projects.data ?? []).map((p) => ({ id: p.id, title: p.title }))} />
    </section>
  );
}
